// SFInternationalShippingModal.tsx
// Dedicated 香港 宅配 modal for HK orders shipping via SF Express International (IUOP).
// Replaces the Hsinchu TW home-delivery flow when shipping_country === 'HK'.
//
// Flow:
//   1. Pre-fill receiver fields from the order (Magnus can correct typos)
//   2. POST /api/storefront/orders/:id/sf/create with { receiver } override
//   3. On success: render label / invoice / cancel buttons
//   4. On failure: show last error, let admin retry
//
// Waybills are created ONLY here — there is no payment-time auto-create.
// Metanoia is preorder, so Magnus opens this after the Korea batch arrives
// and the parcel is actually ready to hand to 順豐.

import React, { useEffect, useMemo, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  Typography,
  Box,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  IconButton,
  Chip,
  Alert,
  AlertTitle,
  CircularProgress,
  Paper,
  Divider,
  Tooltip,
} from '@mui/material';
import {
  FlightTakeoff as FlightIcon,
  Close as CloseIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Print as PrintIcon,
  Receipt as ReceiptIcon,
  Cancel as CancelIcon,
  Search as QueryIcon,
  LocalShipping as ShippingIcon,
} from '@mui/icons-material';
import api from '../services/api';
import axios from 'axios';
import { SF_REGIONS, SfCountryCode, SfIntlRegion } from '../config/intlRegions';

// ─── Types ──────────────────────────────────────────────────────────────────

export interface SFShippingResult {
  sfWaybillNo: string;
  labelUrl?: string | null;
  invoiceUrl?: string | null;
  customerOrderNo?: string | null;
}

interface SFInternationalShippingModalProps {
  open: boolean;
  onClose: () => void;
  order: any; // UnifiedOrder with .original pointing at storefront orders row
  enqueueSnackbar: (message: string, options?: any) => void;
  onShippingPrepared?: (data: SFShippingResult) => void;
  // Destination country for this waybill. Defaults to 'HK' so legacy call sites
  // (and legacy intl_hk orders) keep the exact HK behavior. SF-carried regions
  // only — an SG (Parxl) order must never reach this modal, so the type excludes
  // it rather than silently falling back to HK.
  country?: SfCountryCode;
}

// ─── Helpers ────────────────────────────────────────────────────────────────

// Strip the region's international dialing code if the operator pasted it in.
const sanitizePhone = (phone: string, areaCode: string): string => {
  if (!phone) return '';
  let clean = phone.replace(/\D/g, '');
  // e.g. HK '852' / MO '853' — an 8-digit local number prefixed with the 3-digit
  // area code arrives as 11 digits.
  if (areaCode && clean.startsWith(areaCode) && clean.length === areaCode.length + 8) {
    clean = clean.substring(areaCode.length);
  }
  // HK/MO mobile numbers are 8 digits
  return clean.slice(0, 15);
};

const formatAddressLine = (raw: any): string => {
  if (!raw) return '';
  if (typeof raw === 'string') return raw;
  if (typeof raw === 'object') {
    return [raw.address1, raw.address2, raw.city, raw.province].filter(Boolean).join(', ');
  }
  return String(raw);
};

// First regionThird district for a regionSecond, or '' when the region has no
// third tier (e.g. Macau).
const firstDistrict = (region: SfIntlRegion, regionSecond: string): string =>
  region.districts?.[regionSecond]?.[0]?.value || '';

// ─── Component ──────────────────────────────────────────────────────────────

const SFInternationalShippingModal: React.FC<SFInternationalShippingModalProps> = ({
  open,
  onClose,
  order,
  enqueueSnackbar,
  onShippingPrepared,
  country = 'HK',
}) => {
  const region = SF_REGIONS[country] || SF_REGIONS.HK;
  const areaCode = region.phoneAreaCode;
  const orig = order?.original || {};

  // Detect existing waybill so we can switch the modal into "review/print" mode
  const existingWaybill: string | null = orig.sf_waybill_no || null;
  const existingLabelUrl: string | null = orig.sf_label_url || null;
  const existingInvoiceUrl: string | null = orig.sf_invoice_url || null;
  const existingCancelledAt: string | null = orig.sf_cancelled_at || null;
  const existingLastError: string | null = orig.sf_last_error || null;

  const [loading, setLoading] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [querying, setQuerying] = useState(false);

  // Snapshot after a successful create — takes priority over DB-sourced existing*
  const [created, setCreated] = useState<SFShippingResult | null>(null);
  const [cancelled, setCancelled] = useState<boolean>(!!existingCancelledAt);
  const [lastError, setLastError] = useState<string | null>(existingLastError);

  const effectiveWaybill = created?.sfWaybillNo || existingWaybill;
  const effectiveLabelUrl = created?.labelUrl ?? existingLabelUrl;
  const effectiveInvoiceUrl = created?.invoiceUrl ?? existingInvoiceUrl;

  // ── Pre-fill receiver fields ───────────────────────────────────────────
  const initialAddress = useMemo(
    () => formatAddressLine(orig.shipping_address || order?.shippingAddress),
    [orig.shipping_address, order?.shippingAddress]
  );
  const initialRegion2 = useMemo(() => region.guessRegionSecond(initialAddress), [region, initialAddress]);

  const [receiver, setReceiver] = useState(() => ({
    contact: order?.customerName || orig.customer_name || '',
    phoneNo: sanitizePhone(order?.customerPhone || orig.customer_phone || '', areaCode),
    email: order?.customerEmail || orig.customer_email || '',
    address: initialAddress,
    regionSecond: initialRegion2,
    regionThird: firstDistrict(region, initialRegion2),
    certCardNo: '',
    certType: '001',
  }));

  // If a different order (or country) is selected while modal is mounted, reset the form
  useEffect(() => {
    if (!open) return;
    const addr = formatAddressLine(orig.shipping_address || order?.shippingAddress);
    const r2 = region.guessRegionSecond(addr);
    setReceiver({
      contact: order?.customerName || orig.customer_name || '',
      phoneNo: sanitizePhone(order?.customerPhone || orig.customer_phone || '', areaCode),
      email: order?.customerEmail || orig.customer_email || '',
      address: addr,
      regionSecond: r2,
      regionThird: firstDistrict(region, r2),
      certCardNo: '',
      certType: '001',
    });
    setCreated(null);
    setCancelled(!!orig.sf_cancelled_at);
    setLastError(orig.sf_last_error || null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, order?.orderId, country]);

  // Keep regionThird valid when regionSecond changes (regions without a third tier
  // clear it to '').
  useEffect(() => {
    const districts = region.districts?.[receiver.regionSecond] || [];
    if (districts.length > 0) {
      if (!districts.find((d) => d.value === receiver.regionThird)) {
        setReceiver((prev) => ({ ...prev, regionThird: districts[0].value }));
      }
    } else if (receiver.regionThird) {
      setReceiver((prev) => ({ ...prev, regionThird: '' }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [receiver.regionSecond, region]);

  // ── Handlers ───────────────────────────────────────────────────────────

  const handleChange = (field: keyof typeof receiver) => (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const value = e.target.value;
    setReceiver((prev) => ({ ...prev, [field]: value }));
  };

  const handleSelectChange = (field: keyof typeof receiver) => (e: any) => {
    setReceiver((prev) => ({ ...prev, [field]: e.target.value }));
  };

  const hasDistricts = !!region.districts?.[receiver.regionSecond]?.length;

  const validate = (): string | null => {
    if (!receiver.contact.trim()) return '請輸入收件人姓名';
    if (!receiver.phoneNo.trim()) return `請輸入${region.nameZh}手機號碼`;
    if (receiver.phoneNo.replace(/\D/g, '').length < 7) return `${region.nameZh}手機號碼至少 7 碼`;
    if (!receiver.address.trim()) return `請輸入${region.nameZh}地址`;
    if (!receiver.regionSecond) return '請選擇區域';
    if (hasDistricts && !receiver.regionThird) return '請選擇分區';
    return null;
  };

  const handleSubmit = async () => {
    if (loading) return;
    const err = validate();
    if (err) {
      enqueueSnackbar(err, { variant: 'error' });
      return;
    }

    setLoading(true);
    setLastError(null);

    try {
      const body = {
        country,
        receiver: {
          contact: receiver.contact.trim(),
          phoneNo: sanitizePhone(receiver.phoneNo, areaCode),
          telNo: sanitizePhone(receiver.phoneNo, areaCode),
          phoneAreaCode: areaCode,
          telAreaCode: areaCode,
          email: receiver.email.trim(),
          address: receiver.address.trim(),
          regionFirst: region.regionFirst,
          regionSecond: receiver.regionSecond,
          regionThird: receiver.regionThird,
          certType: receiver.certType || '001',
          certCardNo: receiver.certCardNo.trim() || '',
        },
      };

      const orderId = order?.orderId || order?.orderNumber;
      const response = await api.post(
        `/api/storefront/orders/${encodeURIComponent(orderId)}/sf/create`,
        body
      );

      if (response.data?.success) {
        const result: SFShippingResult = {
          sfWaybillNo: response.data.sfWaybillNo,
          labelUrl: response.data.labelUrl,
          invoiceUrl: response.data.invoiceUrl,
          customerOrderNo: response.data.customerOrderNo,
        };
        setCreated(result);
        enqueueSnackbar(`順豐單已建立：${result.sfWaybillNo}`, { variant: 'success' });
        if (onShippingPrepared) onShippingPrepared(result);
      } else {
        throw new Error(response.data?.error || '建立順豐單失敗');
      }
    } catch (error: any) {
      let msg = '建立順豐單失敗';
      if (axios.isAxiosError(error)) {
        msg = error.response?.data?.error || error.message;
      } else if (error instanceof Error) {
        msg = error.message;
      }
      setLastError(msg);
      enqueueSnackbar(msg, { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleOpenLabel = () => {
    if (!effectiveLabelUrl) {
      enqueueSnackbar('此訂單尚未取得出貨單連結', { variant: 'warning' });
      return;
    }
    window.open(effectiveLabelUrl, '_blank', 'noopener,noreferrer');
  };

  const handleOpenInvoice = () => {
    if (!effectiveInvoiceUrl) {
      enqueueSnackbar('此訂單尚未取得發票連結', { variant: 'warning' });
      return;
    }
    window.open(effectiveInvoiceUrl, '_blank', 'noopener,noreferrer');
  };

  const handleCancel = async () => {
    if (cancelling) return;
    if (!effectiveWaybill) return;
    const confirmed = window.confirm(
      `確定要取消順豐單 ${effectiveWaybill}？\n\n（順豐取件後不一定能取消）`
    );
    if (!confirmed) return;

    setCancelling(true);
    try {
      const orderId = order?.orderId || order?.orderNumber;
      const response = await api.post(
        `/api/storefront/orders/${encodeURIComponent(orderId)}/sf/cancel`
      );
      if (response.data?.success) {
        setCancelled(true);
        enqueueSnackbar('順豐單已取消', { variant: 'success' });
      } else {
        throw new Error(response.data?.error || '取消失敗');
      }
    } catch (error: any) {
      let msg = '取消順豐單失敗';
      if (axios.isAxiosError(error)) {
        msg = error.response?.data?.error || error.message;
      } else if (error instanceof Error) {
        msg = error.message;
      }
      enqueueSnackbar(msg, { variant: 'error' });
    } finally {
      setCancelling(false);
    }
  };

  const handleQuery = async () => {
    if (querying) return;
    if (!effectiveWaybill) return;
    setQuerying(true);
    try {
      const orderId = order?.orderId || order?.orderNumber;
      const response = await api.get(
        `/api/storefront/orders/${encodeURIComponent(orderId)}/sf/query`
      );
      const sfResp = response.data?.sfResponse || {};
      const status = sfResp?.data?.[0]?.routeList?.[0]?.opCode || sfResp?.data?.status || 'OK';
      enqueueSnackbar(`順豐狀態: ${status}`, { variant: 'info', autoHideDuration: 6000 });
      console.log('[SF query]', sfResp);
    } catch (error: any) {
      const msg = axios.isAxiosError(error) ? error.response?.data?.error || error.message : error?.message;
      enqueueSnackbar(`查詢失敗: ${msg}`, { variant: 'error' });
    } finally {
      setQuerying(false);
    }
  };

  if (!order) return null;

  const hasWaybill = !!effectiveWaybill;
  const items = order?.items || [];

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box display="flex" alignItems="center" gap={1}>
            <FlightIcon color="primary" />
            <span>{region.nameZh} 宅配 (順豐國際) — 訂單 #{order.orderNumber}</span>
            <Chip
              label={country}
              color="info"
              size="small"
              sx={{ ml: 1, fontWeight: 700 }}
            />
            {hasWaybill && !cancelled && (
              <Chip
                label="已建立順豐單"
                color="success"
                size="small"
                sx={{ ml: 1 }}
                icon={<CheckCircleIcon />}
              />
            )}
            {cancelled && (
              <Chip
                label="已取消"
                color="default"
                size="small"
                sx={{ ml: 1 }}
                icon={<CancelIcon />}
              />
            )}
          </Box>
          <Tooltip title="關閉">
            <IconButton onClick={onClose}>
              <CloseIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </DialogTitle>

      <DialogContent>
        <Alert severity="info" sx={{ mb: 2 }}>
          <AlertTitle>{region.nameZh}國際出貨 (SF Express)</AlertTitle>
          確認收件人姓名、手機、地址與分區後點選「建立順豐單」。成功後會取得
          sfWaybillNo、出貨單連結與發票連結。{region.customsNote}
        </Alert>

        {hasWaybill && (
          <Paper sx={{ p: 2, mb: 3, bgcolor: cancelled ? '#f5f5f5' : '#e8f5e9' }}>
            <Typography variant="h6" gutterBottom color={cancelled ? 'text.secondary' : 'success.main'}>
              {cancelled ? (
                <>
                  <CancelIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                  順豐單已取消
                </>
              ) : (
                <>
                  <CheckCircleIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                  順豐單資訊
                </>
              )}
            </Typography>

            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2">SF Waybill No</Typography>
                <Typography fontWeight="bold" sx={{ fontFamily: 'monospace' }}>
                  {effectiveWaybill}
                </Typography>
              </Grid>
              {created?.customerOrderNo && (
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2">Customer Order No</Typography>
                  <Typography sx={{ fontFamily: 'monospace' }}>{created.customerOrderNo}</Typography>
                </Grid>
              )}

              {!cancelled && (
                <Grid item xs={12}>
                  <Box display="flex" flexWrap="wrap" gap={1} mt={1}>
                    <Button
                      variant="contained"
                      color="primary"
                      startIcon={<PrintIcon />}
                      onClick={handleOpenLabel}
                      disabled={!effectiveLabelUrl}
                    >
                      列印出貨單
                    </Button>
                    <Button
                      variant="outlined"
                      color="primary"
                      startIcon={<ReceiptIcon />}
                      onClick={handleOpenInvoice}
                      disabled={!effectiveInvoiceUrl}
                    >
                      列印發票
                    </Button>
                    <Button
                      variant="outlined"
                      color="info"
                      startIcon={querying ? <CircularProgress size={16} /> : <QueryIcon />}
                      onClick={handleQuery}
                      disabled={querying}
                    >
                      查詢物流
                    </Button>
                    <Button
                      variant="outlined"
                      color="error"
                      startIcon={cancelling ? <CircularProgress size={16} /> : <CancelIcon />}
                      onClick={handleCancel}
                      disabled={cancelling}
                      sx={{ ml: 'auto' }}
                    >
                      {cancelling ? '取消中...' : '取消順豐單'}
                    </Button>
                  </Box>
                </Grid>
              )}
            </Grid>
          </Paper>
        )}

        {lastError && !hasWaybill && (
          <Alert severity="error" sx={{ mb: 2 }} icon={<ErrorIcon />}>
            <AlertTitle>上次建立失敗</AlertTitle>
            {lastError}
          </Alert>
        )}

        {/* Receiver (HK) */}
        <Typography variant="h6" sx={{ mt: 1, mb: 1 }}>
          <ShippingIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
          收件人 ({region.nameZh})
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="姓名 Contact"
              value={receiver.contact}
              onChange={handleChange('contact')}
              required
              disabled={hasWaybill && !cancelled}
              inputProps={{ maxLength: 50 }}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label={`${region.nameZh}手機 Phone (+${areaCode})`}
              value={receiver.phoneNo}
              onChange={handleChange('phoneNo')}
              required
              disabled={hasWaybill && !cancelled}
              placeholder="8 碼手機號"
              helperText={`${region.nameZh}手機 8 碼，前面不要加 ${areaCode} / +${areaCode}`}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Email"
              value={receiver.email}
              onChange={handleChange('email')}
              disabled={hasWaybill && !cancelled}
              type="email"
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <InputLabel>區域 Region</InputLabel>
              <Select
                value={receiver.regionSecond}
                label="區域 Region"
                onChange={handleSelectChange('regionSecond')}
                disabled={hasWaybill && !cancelled}
              >
                {region.regionOptions.map((r) => (
                  <MenuItem key={r.value} value={r.value}>
                    {r.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {hasDistricts && (
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>分區 District</InputLabel>
                <Select
                  value={receiver.regionThird}
                  label="分區 District"
                  onChange={handleSelectChange('regionThird')}
                  disabled={hasWaybill && !cancelled}
                >
                  {(region.districts?.[receiver.regionSecond] || []).map((d) => (
                    <MenuItem key={d.value} value={d.value}>
                      {d.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          )}
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label={country === 'HK' ? 'HKID (選填)' : '證件號碼 (選填)'}
              value={receiver.certCardNo}
              onChange={handleChange('certCardNo')}
              disabled={hasWaybill && !cancelled}
              helperText="服飾進口免關稅，一般不需填"
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              label="地址 Address"
              value={receiver.address}
              onChange={handleChange('address')}
              multiline
              minRows={2}
              required
              disabled={hasWaybill && !cancelled}
              helperText="完整地址（樓層 / 室號 / 街名 / 大廈名）"
            />
          </Grid>
        </Grid>

        {/* Items summary */}
        <Divider sx={{ my: 3 }} />
        <Typography variant="h6" sx={{ mb: 1 }}>訂單品項</Typography>
        <Paper variant="outlined" sx={{ p: 2, mb: 2, bgcolor: '#fafafa' }}>
          {items.length === 0 && (
            <Typography variant="body2" color="text.secondary">（無品項資料）</Typography>
          )}
          {items.map((it: any, idx: number) => (
            <Box
              key={it.id || idx}
              sx={{
                display: 'grid',
                gridTemplateColumns: '1fr 80px 100px',
                gap: 1,
                alignItems: 'center',
                py: 0.5,
                borderBottom: idx === items.length - 1 ? 'none' : '1px solid #eee',
              }}
            >
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  {it.productName}
                </Typography>
                {it.variantOrSpec && (
                  <Typography variant="caption" color="text.secondary">
                    {it.variantOrSpec}
                  </Typography>
                )}
                {it.sku && (
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                    SKU: {it.sku}
                  </Typography>
                )}
              </Box>
              <Typography variant="body2" align="center">
                x {it.quantity}
              </Typography>
              <Typography variant="body2" align="right" color="text.secondary">
                {it.originalItem?.cart_discount_price
                  ? `NT$${Math.floor(it.originalItem.cart_discount_price)}`
                  : '—'}
              </Typography>
            </Box>
          ))}
          <Divider sx={{ my: 1 }} />
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="subtitle2">訂單金額 (申報用)</Typography>
            <Typography variant="subtitle2" color="primary">
              NT${Math.floor(order.totalAmount || 0).toLocaleString()}
            </Typography>
          </Box>
        </Paper>

      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} color="secondary">
          關閉
        </Button>
        {!hasWaybill && (
          <Button
            onClick={handleSubmit}
            color="primary"
            variant="contained"
            disabled={loading}
            startIcon={loading ? <CircularProgress size={16} /> : <FlightIcon />}
          >
            {loading ? '建立中...' : '建立順豐單'}
          </Button>
        )}
        {hasWaybill && !cancelled && effectiveLabelUrl && (
          <Button
            onClick={handleOpenLabel}
            color="primary"
            variant="contained"
            startIcon={<PrintIcon />}
          >
            列印出貨單
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default SFInternationalShippingModal;
